import type {
  ComponentFormValues,
  ComponentTypeCreateInput,
  ComponentTypeUpdateInput,
  OrderComponentFormValues,
  StockAdjustFormValues,
  StockPurchaseFormValues,
} from '@oficina/shared';
import { api } from '@/lib/api';
import type { ComponentItem, ComponentType, OrderComponentsData, Paginated, RemovalResult, StockMovementRow, StockOverview, StockResult } from '@/types/api';
import type { CatalogListParams } from './catalog.service';

/** Recurso de estoque: produtos ou componentes (as rotas de estoque são iguais para os dois). */
export type StockKind = 'products' | 'components';

export const stockService = {
  overview: () => api.get<StockOverview>('/stock/overview'),
  batch: (input: {
    supplier?: string | null;
    invoice?: string | null;
    receivedAt?: string | null;
    items: { kind: 'product' | 'component'; id: string; quantity: number; unitCost?: number; updateCost?: boolean }[];
  }) => api.post<{ items: number; units: number }>('/stock/batch', input),
  /** Entrada fiscal atômica: estoque + despesa + anexo da NF. */
  batchWithReceipt: (input: {
    supplierId?: string | null;
    supplierName?: string | null;
    invoiceNumber?: string | null;
    invoiceIssuedAt?: string | null;
    dueDate?: string | null;
    file?: File | null;
    items: { kind: 'product' | 'component'; id: string; quantity: number; unitCost?: number; updateCost?: boolean }[];
  }) => {
    const form = new FormData();
    form.append('data', JSON.stringify({ ...input, file: undefined }));
    if (input.file) form.append('invoice', input.file);
    return api.upload<{ items: number; units: number; receiptId: string; expenseId: string }>('/stock/receipts', form);
  },
  purchase: (kind: StockKind, id: string, input: StockPurchaseFormValues) =>
    api.post<StockResult>(`/${kind}/${id}/stock/purchase`, input),
  adjust: (kind: StockKind, id: string, input: StockAdjustFormValues) =>
    api.post<StockResult>(`/${kind}/${id}/stock/adjust`, input),
  movements: (kind: StockKind, id: string, page = 1) =>
    api.get<Paginated<StockMovementRow>>(`/${kind}/${id}/stock/movements`, { page, pageSize: 30 }),
};

export const componentsService = {
  list: (params: CatalogListParams = {}, signal?: AbortSignal) =>
    api.get<Paginated<ComponentItem>>('/components', { pageSize: 500, ...params }, signal),
  create: (input: ComponentFormValues) => api.post<ComponentItem>('/components', input),
  update: (id: string, input: Partial<ComponentFormValues>) => api.patch<ComponentItem>(`/components/${id}`, input),
  remove: (id: string) => api.delete<RemovalResult>(`/components/${id}`),
};

export const componentTypesService = {
  list: (includeInactive = false) => api.get<ComponentType[]>('/component-types', { includeInactive }),
  create: (input: ComponentTypeCreateInput) => api.post<ComponentType>('/component-types', input),
  update: (id: string, input: ComponentTypeUpdateInput) => api.patch<ComponentType>(`/component-types/${id}`, input),
  remove: (id: string) => api.delete<RemovalResult>(`/component-types/${id}`),
};

/** Componentes usados em uma OS (uso interno). */
export const orderComponentsService = {
  list: (orderId: string) => api.get<OrderComponentsData>(`/service-orders/${orderId}/components`),
  add: (orderId: string, input: OrderComponentFormValues) =>
    api.post<OrderComponentsData>(`/service-orders/${orderId}/components`, input),
  remove: (orderId: string, usageId: string) =>
    api.delete<OrderComponentsData>(`/service-orders/${orderId}/components/${usageId}`),
};
